// =====================================================
// API Retry Utility
// AdminMunicipal v6.0 - ERR-HIGH-003 Fix
// Wraps async functions with configurable retry logic
// =====================================================

import { logger } from '../logger';

/**
 * Options for retry behavior
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds before first retry (default: 1000ms) */
  baseDelay?: number;
  /** Custom predicate to determine if an error should trigger a retry */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Default retry predicate - retries on network errors and 5xx server errors
 * Does NOT retry on 4xx client errors (auth, validation, not found, etc.)
 */
function defaultShouldRetry(error: unknown): boolean {
  // Network errors (no response from server)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Supabase errors have specific structure
  if (error && typeof error === 'object' && 'code' in error) {
    const supabaseError = error as { code?: string; message?: string; statusCode?: number };

    // Don't retry authentication errors
    if (supabaseError.code === 'PGRST301') return false; // JWT expired (should re-auth)
    if (supabaseError.code === 'PGRST302') return false; // JWT invalid

    // Don't retry permission errors
    if (supabaseError.code === 'PGRST301') return false;

    // Don't retry not found errors
    if (supabaseError.code === 'PGRST116') return false;

    // Don't retry validation errors
    if (supabaseError.code?.startsWith('23')) return false; // PostgreSQL constraint violations

    // Check HTTP status codes if available
    if (supabaseError.statusCode) {
      // Don't retry 4xx client errors
      if (supabaseError.statusCode >= 400 && supabaseError.statusCode < 500) {
        return false;
      }
      // Retry 5xx server errors
      if (supabaseError.statusCode >= 500) {
        return true;
      }
    }

    // Retry on generic connection/timeout errors
    if (supabaseError.message?.toLowerCase().includes('timeout')) return true;
    if (supabaseError.message?.toLowerCase().includes('connection')) return true;
    if (supabaseError.message?.toLowerCase().includes('network')) return true;
  }

  // Conservative default: don't retry unknown errors
  return false;
}

/**
 * Wraps an async function with retry logic for transient errors
 *
 * Features:
 * - Exponential backoff between retries (1s, 2s, 4s, ...)
 * - Only retries on network/5xx errors (not 4xx client errors)
 * - Configurable retry attempts and delays
 * - Proper error logging
 *
 * @example
 * ```typescript
 * const data = await withRetry(() =>
 *   supabase.from('table').select()
 * );
 * ```
 *
 * @example with options
 * ```typescript
 * const data = await withRetry(
 *   () => supabase.from('table').select(),
 *   { maxRetries: 5, baseDelay: 2000 }
 * );
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    shouldRetry = defaultShouldRetry,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Execute the function
      const result = await fn();

      // If this wasn't the first attempt, log success
      if (attempt > 0) {
        logger.log(`[withRetry] Succeeded on attempt ${attempt + 1}/${maxRetries + 1}`);
      }

      return result;
    } catch (error) {
      lastError = error;

      // Check if we should retry this error
      const canRetry = shouldRetry(error);
      const hasMoreAttempts = attempt < maxRetries;

      if (canRetry && hasMoreAttempts) {
        // Calculate exponential backoff delay: baseDelay * 2^attempt
        const delay = baseDelay * Math.pow(2, attempt);

        logger.warn(
          `[withRetry] Attempt ${attempt + 1}/${maxRetries + 1} failed (retryable error), retrying in ${delay}ms...`,
          error
        );

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // Either error is not retryable, or we've exhausted attempts
        if (!canRetry) {
          logger.warn(`[withRetry] Error not retryable, failing immediately:`, error);
        } else {
          logger.warn(`[withRetry] All ${maxRetries + 1} attempts exhausted:`, error);
        }
        break;
      }
    }
  }

  // All retries exhausted or error not retryable, throw the last error
  throw lastError;
}
